import React, { useCallback, useEffect, useState } from "react";
import { GENERATE_REPORT } from "../../../api/apiUrls";
import Select from "react-select";
import axios from "axios";
import { packageCodes, stStatusOptions } from "../../../static/dropdown";
import { buildCondition } from "../../../static/hook";
import dayjs from "dayjs";
import AppTable from "./AppTable";

const AppDashboard = () => {
  const [dateFilterRange, setDateFilterRange] = useState([]);
  const [appicationReport, setAppicationReport] = useState(null);
  const [appicationLoading, setAppicationLoading] = useState(false);
  const [teamAppicationReport, setTeamAppicationReport] = useState(null);
  const [teamAppicationLoading, setTeamAppicationLoading] = useState(false);
  const [uniAppicationReport, setUniAppicationReport] = useState(null);
  const [uniAppicationLoading, setUniAppicationLoading] = useState(false);
  const [filterDropdown, setFilterDropdown] = useState({
    intakeOptions: [], // Example format: [{ name: 'Intake1', value: 1 }]
    locationOptions: [],
    checklistOptions: [],
    categoryOptions: [],
  });

  const [filterOptions, setFilterOptions] = useState({
    location: [],
    package: [],
    intake: [],
    checklist: [],
    course: [],
    mandatory: [],
    ststatus: [],
    category: [],
  });

  const fetchBaseQuery = useCallback(async (query) => {
    if (!query?.trim()) {
      console.warn("No query found to execute");
      return null;
    }
    try {
      const { data } = await axios.post(GENERATE_REPORT, { query });
      return data || null;
    } catch (error) {
      console.error(
        "Error occurred in fetchBaseQuery:",
        error?.response?.data?.message || error.message || "Unknown error"
      );
      return null;
    }
  }, []);

  const fetchFilterDropdown = useCallback(async () => {
    try {
      const queries = {
        intakeOptions:
          "SELECT IntakeCode AS 'Code', Intakename AS 'Name' FROM intakemas",
        locationOptions:
          "SELECT LocCode AS 'Code', locname AS 'Name' FROM locmas",
        checklistOptions:
          "SELECT DoctypeCode AS 'Code', DoctypeName AS 'Name' FROM DocTypeMas WHERE DoctypeCode NOT IN (11, 12, 13, 17, 26)",
        categoryOptions:
          "SELECT CatCode AS 'Code', CatName AS 'Name' FROM catmas",
      };

      const fetchPromises = Object.entries(queries).map(
        async ([key, query]) => {
          const data = await fetchBaseQuery(query);
          return { key, data };
        }
      );

      const results = await Promise.all(fetchPromises);

      const updatedDropdowns = results.reduce((acc, { key, data }) => {
        if (data?.length) {
          acc[key] = data.map((item) => ({
            label: item.NAME || "Unknown",
            value: item.CODE || "N/A",
          }));
        } else {
          console.warn(`No data received for ${key}`);
        }
        return acc;
      }, {});

      if (Object.keys(updatedDropdowns).length) {
        setFilterDropdown((prev) => ({ ...prev, ...updatedDropdowns }));
      }
    } catch (error) {
      console.error("Error fetching filter dropdown options:", error.message);
    }
  }, [fetchBaseQuery]);

  const fetchApplicationReports = async () => {
    setAppicationLoading(true);
    try {
      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.ststatus", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode";
      const applicationWiseBaseQuery =
        "SELECT COUNT(DISTINCT a.applicationId)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode";
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      let currDate = dayjs().format("YYYY-MM-DD");

      const queries = {
        total_students: `${baseQuery} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        cl_final_student: `${baseQuery} WHERE s.uinliststatus IN (1,4,5,6) ${whereClause}`,
        // total_application: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) ${whereClause}`,
        total_approved_application: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1  ${whereClause}`,
        total_approved_application_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.publicorprivate = 'Public' ${whereClause}`,
        total_approved_application_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.publicorprivate = 'Private' ${whereClause}`,
        total_approved_application_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        blank_start_date: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.Applicationstartdatenew IS NULL ${whereClause}`,
        blank_start_date_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.Applicationstartdatenew IS NULL AND a.publicorprivate = 'Public' ${whereClause}`,
        blank_start_date_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.Applicationstartdatenew IS NULL AND a.publicorprivate = 'Private' ${whereClause}`,
        blank_start_date_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.Applicationstartdatenew IS NULL AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        blank_deadline: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.deadline IS NULL ${whereClause}`,
        blank_deadline_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.deadline IS NULL AND a.publicorprivate = 'Public' ${whereClause}`,
        blank_deadline_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.deadline IS NULL AND a.publicorprivate = 'Private' ${whereClause}`,
        blank_deadline_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.deadline IS NULL AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        blank_firstStep_deadline: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.Firststepdeadlinedatenew IS NULL OR a.Firststepdeadlinedatenew = '') ${whereClause}`,
        blank_firstStep_deadline_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.Firststepdeadlinedatenew IS NULL OR a.Firststepdeadlinedatenew = '') AND a.publicorprivate = 'Public' ${whereClause}`,
        blank_firstStep_deadline_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.Firststepdeadlinedatenew IS NULL OR a.Firststepdeadlinedatenew = '') AND a.publicorprivate = 'Private' ${whereClause}`,
        blank_firstStep_deadline_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.Firststepdeadlinedatenew IS NULL OR a.Firststepdeadlinedatenew = '') AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        not_applied_yet: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.plannerstatuscode IN (50, 0) OR a.plannerstatuscode IS NULL OR a.plannerstatuscode = '' ) ${whereClause}`,
        not_applied_yet_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.plannerstatuscode IN (50, 0) OR a.plannerstatuscode IS NULL OR a.plannerstatuscode = '' ) AND a.publicorprivate = 'Public' ${whereClause}`,
        not_applied_yet_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.plannerstatuscode IN (50, 0) OR a.plannerstatuscode IS NULL OR a.plannerstatuscode = '' ) AND a.publicorprivate = 'Private' ${whereClause}`,
        not_applied_yet_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.plannerstatuscode IN (50, 0) OR a.plannerstatuscode IS NULL OR a.plannerstatuscode = '' ) AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        deadline_missed: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode NOT IN (47, 48, 49) AND a.deadline IS NOT NULL AND CAST(a.deadline AS DATE) < '${currDate}' ${whereClause}`,
        deadline_missed_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode NOT IN (47, 48, 49) AND a.deadline IS NOT NULL AND CAST(a.deadline AS DATE) < '${currDate}' AND a.publicorprivate = 'Public' ${whereClause}`,
        deadline_missed_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode NOT IN (47, 48, 49) AND a.deadline IS NOT NULL AND CAST(a.deadline AS DATE) < '${currDate}' AND a.publicorprivate = 'Private' ${whereClause}`,
        deadline_missed_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode NOT IN (47, 48, 49) AND a.deadline IS NOT NULL AND CAST(a.deadline AS DATE) < '${currDate}' AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        application_firstStep_applied: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 45 ${whereClause}`,
        application_firstStep_applied_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 45 AND a.publicorprivate = 'Public' ${whereClause}`,
        application_firstStep_applied_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 45 AND a.publicorprivate = 'Private' ${whereClause}`,
        application_firstStep_applied_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 45 AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        application_applied: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47 ${whereClause}`,
        application_applied_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47  AND a.publicorprivate = 'Public' ${whereClause}`,
        application_applied_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47 AND a.publicorprivate = 'Private' ${whereClause}`,
        application_applied_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47  AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        application_in_process: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IS NOT NULL AND a.plannerstatuscode NOT IN (0, 45, 47, 48, 49, 50) ${whereClause}`,
        application_in_process_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IS NOT NULL AND a.plannerstatuscode NOT IN (0, 45, 47, 48, 49, 50) AND a.publicorprivate = 'Public' ${whereClause}`,
        application_in_process_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IS NOT NULL AND a.plannerstatuscode NOT IN (0, 45, 47, 48, 49, 50) AND a.publicorprivate = 'Private' ${whereClause}`,
        application_in_process_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IS NOT NULL AND a.plannerstatuscode NOT IN (0, 45, 47, 48, 49, 50) AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        application_cancelled: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (48) ${whereClause}`,
        application_cancelled_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (48) AND a.publicorprivate = 'Public' ${whereClause}`,
        application_cancelled_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (48) AND a.publicorprivate = 'Private' ${whereClause}`,
        application_cancelled_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (48) AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
        application_dropped: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (49) ${whereClause}`,
        application_dropped_public: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (49) AND a.publicorprivate = 'Public' ${whereClause}`,
        application_dropped_private: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (49) AND a.publicorprivate = 'Private' ${whereClause}`,
        application_dropped_other: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IN (49) AND (a.publicorprivate = '' OR a.publicorprivate IS NULL) ${whereClause}`,
      };

      const responseObj = {};
      for (const query in queries) {
        const result = await fetchBaseQuery(queries[query]);
        responseObj[query] = {
          count: result[0]?.Column1,
          query: queries[query],
        };
      }
      if (responseObj) {
        setAppicationReport(responseObj);
      }
    } catch (error) {
      console.error(error);
      setAppicationLoading(false);
    } finally {
      setAppicationLoading(false);
    }
  };
  const fetchApplicationReportTeamWise = async () => {
    setTeamAppicationLoading(true);
    try {
      const coordinatorList = await fetchBaseQuery(
        "SELECT um.UserCode, um.UserName, um.Active FROM Usermas um WHERE um.deptcode = 5"
      );

      if (!Array.isArray(coordinatorList) || coordinatorList.length === 0) {
        console.log("No records to execute");
        return [];
      }
      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.ststatus", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode";
      const applicationWiseBaseQuery =
        "SELECT COUNT(DISTINCT a.applicationId)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode";
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      let currDate = dayjs().format("YYYY-MM-DD");

      const queries = {
        total_students: `${baseQuery} WHERE s.StdCode IS NOT NULL ${whereClause}`,
        cl_final_student: `${baseQuery} WHERE s.uinliststatus IN (1,4,5,6) ${whereClause}`,
        total_application: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) ${whereClause}`,
        total_approved_application: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 ${whereClause}`,
        blank_start_date: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.Applicationstartdatenew IS NULL ${whereClause}`,
        blank_deadline: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.deadline IS NULL ${whereClause}`,
        not_applied_yet: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND (a.plannerstatuscode = 50 OR a.plannerstatuscode IS NULL) ${whereClause}`,
        deadline_missed: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 50 AND a.deadline IS NOT NULL AND CAST(a.deadline AS DATE) < '${currDate}' ${whereClause}`,
        application_applied: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode = 47 AND a.status IN ('Rejected', 'Admission Given', 'Admission Accepted By Student', 'Final Admission Accepted', 'Applied') ${whereClause}`,
        application_in_process: `${applicationWiseBaseQuery} WHERE s.uinliststatus IN (1,4,5,6) AND a.statusFromStudent = 1 AND a.statusFromYGTeam = 1 AND a.plannerstatuscode IS NOT NULL AND a.plannerstatuscode NOT IN (47, 48, 49, 50,'') AND a.status NOT IN ('Rejected', 'Admission Given', 'Admission Accepted By Student', 'Final Admission Accepted', 'Applied') ${whereClause}`,
      };

      const responseObj = [];
      for (const item of coordinatorList) {
        const coordinator = item.USERCODE;
        const coordinatorData = {
          name: item?.USERNAME,
          details: {},
        };

        for (const [key, baseQuery] of Object.entries(queries)) {
          // Inject counsellor condition
          const finalQuery = `${baseQuery} AND um.UserCode = '${coordinator}'`;

          try {
            const result = await fetchBaseQuery(finalQuery);
            coordinatorData.details[key] = {
              query: finalQuery,
              count: result[0]?.Column1 || 0,
            };
          } catch (error) {
            console.error(`Error fetching ${key} for ${coordinator}:`, error);
            coordinatorData.details[key] = null;
          }
        }

        responseObj.push(coordinatorData);
      }
      setTeamAppicationReport(responseObj);
    } catch (error) {
      console.error(error);
      setTeamAppicationLoading(false);
    } finally {
      setTeamAppicationLoading(false);
    }
  };
  const fetchApplicationReportUniversityWise = async () => {
    setUniAppicationLoading(true);
    try {
      const universityList = await fetchBaseQuery(
        "SELECT DISTINCT a.universityName AS University, a.CourseName AS Course FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode WHERE a.universityName != '' AND a.CourseName != '' AND a.universityName IS NOT NULL"
      );

      if (!Array.isArray(universityList) || universityList.length === 0) {
        console.log("No records to execute");
        return [];
      }
      const filters = [];

      const conditions = [
        buildCondition("s.intakeCode", filterOptions.intake, true),
        buildCondition("s.packageCode", filterOptions.package, true),
        buildCondition("s.ststatus", filterOptions.package, true),
        buildCondition("s.LocCode", filterOptions.location, true),
        buildCondition("sd.DoctypeCode", filterOptions.checklist),
        buildCondition("s.coursetype", filterOptions.course),
        buildCondition("d.mandatory", filterOptions.mandatory),
      ];

      conditions.forEach((cond) => cond && filters.push(cond));

      // Handle date range
      const rangeCondition =
        dateFilterRange?.length === 2 &&
        typeof dateFilterRange[0] === "string" &&
        typeof dateFilterRange[1] === "string" &&
        dateFilterRange[0].trim() &&
        dateFilterRange[1].trim()
          ? `CAST(s.RegistrDate AS DATE) BETWEEN '${dateFilterRange[0]}' AND '${dateFilterRange[1]}'`
          : "";

      if (rangeCondition) filters.push(rangeCondition);
      const baseQuery =
        "SELECT COUNT(DISTINCT s.StdCode)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode";
      const applicationWiseBaseQuery =
        "SELECT COUNT(DISTINCT a.applicationId)  FROM StudentMas s LEFT JOIN admissionmas a ON s.StdCode = a.StdCode LEFT JOIN Stdassignment sa ON s.StdCode = sa.stdcode LEFT JOIN Usermas um ON um.UserCode = sa.UserCode";
      const whereClause = filters.length ? `AND ${filters.join(" AND ")}` : "";
      let currDate = dayjs().format("YYYY-MM-DD");

      const query = `SELECT DISTINCT a.universityName, a.deadline,a.Firststepdeadlinedatenew, a.Applicationstartdatenew, a.CourseName,  
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  THEN s.StdCode END) AS Approved, 
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  AND a.Applicationstartdatenew IS NULL 
                  THEN s.StdCode END) AS BlankOpenDate,
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  AND a.Firststepdeadlinedatenew IS NULL 
                  THEN s.StdCode END) AS BlankFirstDeadlineDate,
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  AND a.deadline IS NULL 
                  THEN s.StdCode END) AS BlankDeadlineDate,  
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  AND a.plannerstatuscode = 50 
                  THEN s.StdCode END) AS NotApplied, 
        COUNT (CASE 
                WHEN s.uinliststatus IN (1,4,5,6) 
                  AND a.statusFromStudent = 1 
                  AND a.statusFromYGTeam = 1 
                  AND a.plannerstatuscode = 50 
                  AND a.deadline IS NOT NULL 
                  AND CAST(a.deadline AS DATE) < CAST(GETDATE() AS DATE) 
                  THEN s.StdCode END) AS DeadlineMissed 
        FROM StudentMas s LEFT 
        JOIN admissionmas a ON s.StdCode = a.StdCode 
        WHERE a.universityName != '' 
          AND a.CourseName != '' 
          AND a.universityName IS NOT NULL 
          AND a.Applicationstartdatenew IS NOT NULL ${whereClause} 
        GROUP BY  a.universityName, a.CourseName,a.Firststepdeadlinedatenew, a.Applicationstartdatenew, a.deadline ORDER BY a.deadline ASC`;

      let responseObj = [];
      const result = await fetchBaseQuery(query);

      responseObj.push(result);
      setUniAppicationLoading(true);
      console.log("setUniAppicationLoading", responseObj)
      if (responseObj) {
        setUniAppicationLoading(false);
        setUniAppicationReport(result);
      }
    } catch (error) {
      console.error(error);
      setUniAppicationLoading(false);
    } finally {
      setUniAppicationLoading(false);
    }
  };

  const handleChange = (index, value) => {
    const updatedRange = dateFilterRange;
    updatedRange[index] = value; // value will be in 'YYYY-MM-DD' format
    setDateFilterRange(updatedRange);
  };

  const handleFilterChange = () => {
    fetchApplicationReportUniversityWise();
  };

  useEffect(() => {
    fetchFilterDropdown();
    fetchApplicationReports();
    fetchApplicationReportTeamWise();
    fetchApplicationReportUniversityWise();
  }, []);
  return (
    <div>
      <div className="header py-3 border border-light px-2 rounded bg-light">
        <h5>Filter</h5>
        <div className="row g-2 g-lg-3 mb-3">
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="intake-select"
                options={filterDropdown.intakeOptions}
                isMulti
                isClearable
                placeholder="Select Intake"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    intake: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="package-select"
                options={filterDropdown.locationOptions}
                isMulti
                isClearable
                placeholder="Select Branch"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    location: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="ststatus-select"
                options={stStatusOptions}
                isMulti
                isClearable
                placeholder="Select Student Status"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    ststatus: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="category-select"
                options={filterDropdown.categoryOptions}
                isMulti
                isClearable
                placeholder="Select Student Category"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    category: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="package-select"
                options={packageCodes}
                isMulti
                isClearable
                placeholder="Select Package"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    package: selectedOptions
                      ? selectedOptions.map((opt) => parseInt(opt.value))
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-2">
            <div className="form-group">
              <Select
                id="course-select"
                options={[
                  { label: "Master's", value: "M" },
                  { label: "Bachelor's", value: "B" },
                ]}
                isMulti
                isClearable
                placeholder="Select Course"
                onChange={(selectedOptions) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    course: selectedOptions
                      ? selectedOptions.map((opt) => opt.value)
                      : [],
                  }));
                }}
              />
            </div>
          </div>
          <div className="col-md-1 text-end">
            <label htmlFor="">Select Range</label>
          </div>
          <div className="col-md-3">
            <div className="form-group d-flex">
              <input
                type="date"
                className="form-control"
                // value={dateFilterRange[0] || ""}
                onChange={(e) => handleChange(0, e.target.value)}
              />
              <input
                type="date"
                className="form-control"
                // value={dateFilterRange[1] || ""}
                onChange={(e) => handleChange(1, e.target.value)}
              />
            </div>
          </div>
          <div className="form-group col-md-2">
            <button
              type="button"
              className="btn btn-warning btn-sm"
              onClick={handleFilterChange}
            >
              Apply Filter
            </button>
          </div>
        </div>
      </div>
      <div>
        <AppTable
          appicationReport={appicationReport}
          appicationLoading={appicationLoading}
          teamAppicationReport={teamAppicationReport}
          teamAppicationLoading={teamAppicationLoading}
          uniAppicationReport={uniAppicationReport}
          uniAppicationLoading={uniAppicationLoading}
        />
      </div>
    </div>
  );
};

export default AppDashboard;
